/**
 * OtpCountdown — visible countdown for OTP code expiry. Ported from
 * `screens/src/components/OtpCountdown.jsx` with TS types.
 *
 * Behaviour:
 * - Counts down from `seconds` (default 300 = 5 min).
 * - Shows MM:SS while time remains.
 * - When the timer hits 0, swaps the timer for a "Code expired — Resend" CTA.
 * - The `key` of this component should change when a new code is dispatched
 *   (e.g. set `key={lastSentAt}`) so the parent can reset the counter.
 */
import { useEffect, useState, type JSX } from 'react';
import { Clock, RefreshCcw } from 'lucide-react';

interface OtpCountdownProps {
  readonly seconds?: number;
  readonly onResend?: () => void;
}

export function OtpCountdown({ seconds = 300, onResend }: OtpCountdownProps): JSX.Element {
  const [remaining, setRemaining] = useState<number>(seconds);

  useEffect(() => {
    setRemaining(seconds);
  }, [seconds]);

  useEffect(() => {
    if (remaining <= 0) return undefined;
    const id = setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => clearInterval(id);
  }, [remaining]);

  if (remaining <= 0) {
    return (
      <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200">
        <p className="text-xs text-amber-800">
          <span className="font-semibold">Code expired.</span> Request a new one to continue.
        </p>
        {onResend && (
          <button
            type="button"
            onClick={onResend}
            className="inline-flex items-center gap-1 text-xs font-semibold text-nipost-green hover:underline"
          >
            <RefreshCcw className="w-3 h-3" /> Resend
          </button>
        )}
      </div>
    );
  }

  const m = Math.floor(remaining / 60);
  const s = remaining % 60;
  const display = `${m}:${String(s).padStart(2, '0')}`;
  const lowTime = remaining <= 30;

  return (
    <div
      className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border ${
        lowTime ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-gray-50 border-border text-gray-700'
      }`}
    >
      <div className="flex items-center gap-1.5 text-xs">
        <Clock className="w-3.5 h-3.5" />
        <span>
          Code expires in <span className="font-mono font-bold">{display}</span>
        </span>
      </div>
      {onResend && (
        <button
          type="button"
          onClick={onResend}
          className="text-xs font-semibold text-nipost-green hover:underline"
        >
          Resend
        </button>
      )}
    </div>
  );
}
